/* PLYOFOLD homepage runtime — dependency-free port of the design-concept
   prototype (Claude Design V3 motion set). Horizon scrolls an inner
   .page-wrapper on desktop (html/body are overflow:hidden), so all scroll
   plumbing here is scroller-agnostic: a capturing document scroll listener
   catches every scroller, and all geometry is viewport-rect based.
   Reduced motion: scrub/counts/floats/reveals do not register; the fold is
   driven by its slider and all content renders as laid out. */
(function () {
  'use strict';
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  document.documentElement.classList.add('pf-js');

  /* ---------- stable hero height ---------- */
  // Brave and Chrome on iOS resize the page whenever their toolbar slides in or
  // out, so any height in viewport units (svh included) changes mid-scroll and
  // everything below the hero jumps by the toolbar's height. Pin the hero to the
  // height measured at load and only re-measure when the width changes, which
  // means the phone was rotated or the window resized.
  var heroW = 0;
  var touch = window.matchMedia('(pointer: coarse)').matches;
  function pinHeroHeight() {
    // With a mouse, a height-only resize is a real window resize: follow it.
    if (touch && window.innerWidth === heroW) return;
    heroW = window.innerWidth;
    document.documentElement.style.setProperty('--pf-hero-h', window.innerHeight + 'px');
  }
  pinHeroHeight();
  window.addEventListener('resize', pinHeroHeight, { passive: true });

  /* ---------- header state + world ---------- */
  var header = document.querySelector('.pf-header');
  var hero = document.querySelector('.pf-hero');
  var themed = Array.prototype.slice.call(document.querySelectorAll('[data-pf-theme]'));
  function syncHeader() {
    if (header) {
      var solid = true;
      if (hero) solid = hero.getBoundingClientRect().top < -32;
      // Writing an unchanged attribute still counts as a mutation and costs a
      // style pass on every scroll frame, so only write when the state flips.
      if (header.getAttribute('data-solid') !== String(solid)) header.setAttribute('data-solid', String(solid));
    }
    var world = themed.length ? 'dark' : 'light';
    for (var i = 0; i < themed.length; i++) {
      var r = themed[i].getBoundingClientRect();
      if (r.top <= 80 && r.bottom > 80) world = themed[i].getAttribute('data-pf-theme') === 'light' ? 'light' : 'dark';
    }
    if (document.body.getAttribute('data-pf-world') !== world) document.body.setAttribute('data-pf-world', world);
  }

  var ticking = false;
  function onAnyScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(function () {
      try {
        syncHeader();
        foldScrub();
        floats();
      } finally {
        ticking = false;
      }
    });
  }
  // Capture phase: fires for window AND any inner scroller (.page-wrapper).
  document.addEventListener('scroll', onAnyScroll, true);
  window.addEventListener('resize', onAnyScroll);

  /* ---------- mobile nav ---------- */
  var burger = document.querySelector('.pf-burger');
  var mnav = document.querySelector('.pf-mobile-nav');
  if (burger && mnav) {
    var closeNav = function () {
      burger.setAttribute('aria-expanded', 'false');
      mnav.hidden = true;
      document.body.style.overflow = '';
    };
    burger.addEventListener('click', function () {
      var open = burger.getAttribute('aria-expanded') === 'true';
      if (open) { closeNav(); return; }
      burger.setAttribute('aria-expanded', 'true');
      mnav.hidden = false;
      document.body.style.overflow = 'hidden';
    });
    mnav.addEventListener('click', function (e) { if (e.target.closest('a')) closeNav(); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && !mnav.hidden) { closeNav(); burger.focus(); } });
  }

  /* ---------- fold stage (M-10) ---------- */
  var tall, stage, range, chip, openFig, closedFig, steps;
  function wireFold() {
    tall = document.querySelector('.pf-fold-tall');
    stage = document.querySelector('.pf-fold-stage');
    range = document.getElementById('pf-fold-range');
    chip = document.getElementById('pf-fold-chip');
    openFig = document.querySelector('.pf-fold-open');
    closedFig = document.querySelector('.pf-fold-closed');
    steps = Array.prototype.slice.call(document.querySelectorAll('.pf-step'));
    STEPS = steps.map(function (el) { return el.getAttribute('data-status') || el.textContent.trim(); });
    if (range) range.addEventListener('input', onRangeInput);
    setFold(0);
  }
  function onRangeInput() { setFold(Number(range.value) / 100); }
  // Status labels come from the section's data-status attributes so they stay
  // editable in the theme editor; the count follows however many steps render.
  var STEPS = [];

  function setFold(t) {
    if (!stage) return;
    t = Math.min(1, Math.max(0, t));
    stage.style.setProperty('--fold', String(t));
    if (openFig) openFig.setAttribute('aria-hidden', String(t > 0.5));
    if (closedFig) closedFig.setAttribute('aria-hidden', String(t <= 0.5));
    var n = steps.length || 1;
    var step = Math.min(n - 1, Math.floor(t * n));
    if (chip && STEPS[step]) chip.textContent = STEPS[step];
    steps.forEach(function (p, i) { p.setAttribute('data-active', String(i === step)); });
    if (range && Math.abs(Number(range.value) / 100 - t) > 0.01) range.value = String(Math.round(t * 100));
  }
  wireFold();

  function foldScrub() {
    if (reduced || !tall || !stage) return;
    var rect = tall.getBoundingClientRect();
    var total = rect.height - window.innerHeight;
    if (total <= 0) return;
    var t = -rect.top / total;
    if (t >= -0.02 && t <= 1.02) setFold(t);
  }

  /* ---------- carousel (M-15) ---------- */
  var track, prev, next;
  function stepW() {
    var card = track && track.querySelector('.pf-ben-card');
    return card ? card.getBoundingClientRect().width + 24 : 0;
  }
  function syncArrows() {
    if (!track || !prev || !next) return;
    prev.disabled = track.scrollLeft <= 4;
    next.disabled = track.scrollLeft >= track.scrollWidth - track.clientWidth - 4;
  }
  function wireCarousel() {
    track = document.querySelector('.pf-car-track');
    prev = document.querySelector('[data-pf-prev]');
    next = document.querySelector('[data-pf-next]');
    if (!track || !prev || !next) return;
    // Re-wiring after an editor render must not double-bind arrows that were
    // never replaced; a second click listener would scroll two cards at once.
    if (!track.hasAttribute('data-pf-wired')) {
      track.setAttribute('data-pf-wired', 'true');
      prev.addEventListener('click', function () { track.scrollBy({ left: -stepW(), behavior: 'smooth' }); });
      next.addEventListener('click', function () { track.scrollBy({ left: stepW(), behavior: 'smooth' }); });
      track.addEventListener('scroll', syncArrows, { passive: true });
    }
    syncArrows();
  }
  window.addEventListener('resize', syncArrows);
  wireCarousel();

  /* ---------- count-up (M-13) ---------- */
  var statIO = null;
  if (!reduced && 'IntersectionObserver' in window) {
    statIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        statIO.unobserve(entry.target);
        var el = entry.target.querySelector('.pf-stat-value');
        if (!el) return;
        var target = Number(entry.target.getAttribute('data-count') || el.textContent);
        var start = null;
        var tick = function (ts) {
          if (start === null) start = ts;
          var p = Math.min(1, (ts - start) / 1100);
          var eased = 1 - Math.pow(1 - p, 3);
          el.textContent = String(Math.round(target * eased));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      });
    }, { threshold: 0.4 });
    observeStats(document);
  }
  function observeStats(root) {
    if (!statIO) return;
    root.querySelectorAll('.pf-stat').forEach(function (s) { statIO.observe(s); });
  }

  /* ---------- reveals (gated behind html.pf-js in CSS) ---------- */
  var revealIO = null;
  if (!reduced && 'IntersectionObserver' in window) {
    revealIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) { entry.target.classList.add('pf-in'); revealIO.unobserve(entry.target); }
      });
    }, { rootMargin: '0px 0px -10% 0px' });
    showReveals(document);
  } else {
    showReveals(document);
  }
  function showReveals(root) {
    root.querySelectorAll('.pf-reveal').forEach(function (el) {
      if (revealIO) revealIO.observe(el); else el.classList.add('pf-in');
    });
  }

  /* ---------- seam floats (M-14) + wordmark parallax (M-12) ---------- */
  var floatEls = [];
  var wordmark = null;
  function collectFloats() {
    floatEls = Array.prototype.slice.call(document.querySelectorAll('.pf-float'));
    wordmark = document.querySelector('.pf-wordmark');
  }
  collectFloats();
  function clamp1(v) { return Math.max(-1, Math.min(1, v)); }
  // Parallax only where scrolling is driven by a wheel or trackpad. On touch
  // screens the page scrolls on the compositor while these transforms land a
  // frame later from the main thread, so the cards visibly wobble against the
  // page instead of floating.
  var parallax = !reduced && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  function floats() {
    if (!parallax) return;
    var vh = window.innerHeight;
    // Read every position first, then write: interleaving the two forces a
    // fresh layout for each element.
    var shifts = floatEls.map(function (el) {
      var r = el.getBoundingClientRect();
      var p = clamp1((r.top + r.height / 2 - vh / 2) / vh);
      return -p * 2 * Number(el.getAttribute('data-float') || 16);
    });
    var wp = null;
    if (wordmark) {
      var wr = wordmark.parentElement.getBoundingClientRect();
      wp = clamp1((wr.top + wr.height / 2 - vh / 2) / vh);
    }
    floatEls.forEach(function (el, i) {
      el.style.transform = 'translateY(' + shifts[i].toFixed(1) + 'px)';
    });
    if (wordmark) wordmark.style.transform = 'translate(calc(-50% + ' + (wp * -60).toFixed(1) + 'px), -50%)';
  }

  /* ---------- hero video (pause offscreen / reduced motion) ---------- */
  var heroVideo = document.querySelector('.pf-hero-video');
  if (heroVideo) {
    if (reduced) {
      heroVideo.removeAttribute('autoplay');
      heroVideo.pause();
    } else if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        if (entries[0].isIntersecting) {
          var vp = heroVideo.play();
          if (vp && vp.catch) vp.catch(function () {});
        } else {
          heroVideo.pause();
        }
      }, { threshold: 0.05 }).observe(heroVideo);
    }
  }

  /* ---------- sticky buy bar ---------- */
  var buybar = document.querySelector('.pf-buybar');
  if (buybar && hero && 'IntersectionObserver' in window) {
    new IntersectionObserver(function (entries) {
      buybar.setAttribute('data-visible', String(!entries[0].isIntersecting));
    }).observe(hero);
  } else if (buybar && !hero) {
    buybar.setAttribute('data-visible', 'true');
  }

  /* ---------- theme editor ---------- */
  // Shopify replaces a section's whole DOM on every setting change. Everything
  // above holds references to the nodes present at first load, so the fresh
  // ones are never observed: .pf-reveal keeps the opacity:0 the CSS gives it
  // and the section renders as an empty dark band. That is what made editing
  // the engineering photos turn the section black. Re-wire what was swapped.
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (e) {
      var root = e.target && e.target.querySelectorAll ? e.target : document;
      themed = Array.prototype.slice.call(document.querySelectorAll('[data-pf-theme]'));
      wireFold();
      wireCarousel();
      collectFloats();
      showReveals(root);
      observeStats(root);
      syncHeader();
      foldScrub();
      floats();
    });
    // Selecting a section scrolls it into view; make sure it is not left
    // mid-reveal while the merchant is looking straight at it.
    document.addEventListener('shopify:section:select', function (e) {
      var root = e.target && e.target.querySelectorAll ? e.target : document;
      showReveals(root);
    });
  }

  syncHeader();
  foldScrub();
  floats();
})();
